import { defineStore } from 'pinia'
import type {
  AuthRefreshSuccessResponse,
  VerifyOtpSuccessResponse,
} from '../types/api'
import { AdminRole } from '@/modules/Core/constants/adminAccess'
import type { HrPermissionCode } from '@/modules/Core/constants/hrPermissions'
import {
  hasAnyPermission,
  hasPermission as checkPermission,
  parsePermissions,
  toPermissionSet,
} from '@/modules/Core/utils/permissions'
import useUserStore from '@/modules/User/store'

interface State {
  accessToken: string
  refreshToken: string
  roles: AdminRole[]
  permissions: HrPermissionCode[]
  isAuthenticated: boolean
}

const ROLE_ALIASES: Record<string, AdminRole> = {
  HrManager: AdminRole.HrManager,
  HRManager: AdminRole.HrManager,
  Employee: AdminRole.Employee,
}

function parseRoles(roles: string[] | null | undefined): AdminRole[] {
  if (!roles?.length) {
    return []
  }

  const validRoles = new Set(Object.values(AdminRole))

  return roles
    .map((role) => {
      if (validRoles.has(role as AdminRole)) {
        return role as AdminRole
      }

      return ROLE_ALIASES[role]
    })
    .filter((role): role is AdminRole => Boolean(role))
}

function initState() {
  return {
    accessToken: '',
    refreshToken: '',
    roles: [],
    permissions: [],
    isAuthenticated: false,
  } as State
}

export const useAuthStore = defineStore('auth', {
  state: (): State => ({ ...initState() }),
  getters: {
    permissionSet: (state): ReadonlySet<HrPermissionCode> =>
      toPermissionSet(state.permissions),
    hasPermission:
      (state) =>
      (code: HrPermissionCode): boolean =>
        checkPermission(toPermissionSet(state.permissions), code),
    hasAnyPermission:
      (state) =>
      (codes: readonly HrPermissionCode[]): boolean =>
        hasAnyPermission(toPermissionSet(state.permissions), codes),
    isApplicant: (state): boolean => state.roles.includes(AdminRole.Applicant),
  },
  actions: {
    setLoginState(data: VerifyOtpSuccessResponse) {
      this.accessToken = data.accessToken
      this.refreshToken = data.refreshToken
      this.roles = parseRoles(data.roles)
      this.permissions = parsePermissions(data.permissions)
      this.isAuthenticated = true
    },
    setToken(data: AuthRefreshSuccessResponse) {
      this.accessToken = data.accessToken
      this.refreshToken = data.refreshToken

      if (data.roles !== undefined) {
        this.roles = parseRoles(data.roles)
      }

      if (data.permissions !== undefined && data.permissions !== null) {
        this.permissions = parsePermissions(data.permissions)
      }
    },
    logout() {
      useUserStore().clearProfile()
      this.isAuthenticated = false
      this.$reset()
    },
  },
  persist: {
    storage: window.localStorage,
    afterHydrate: ({ store }) => {
      if (store.isAuthenticated && !store.accessToken) {
        store.logout()
        return
      }

      if (store.isAuthenticated && store.permissions.length === 0) {
        store.logout()
      }
    },
  },
})

export default useAuthStore
